import { useState } from 'react';
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  CircularProgress,
} from '@mui/material';
import {
  CheckCircle,
  Cancel,
  Quiz,
  Refresh,
  AdminPanelSettings,
} from '@mui/icons-material';
import Stack from '@/components/Stack';
import { Body, Small } from '@/components/Typography';
import Badge from '@/components/Badge';
import Card from '@/components/Card';
import {
  ConsolidatedAssessment,
  ConsolidatedSkillRequest,
} from '@/services/workersV2API';
import { axiosRubyClient } from '@/util/axios/axiosClient';

type Props = {
  skillRequestId: number;
  assessment: ConsolidatedAssessment | null;
  assessmentPassed: boolean;
  canRetry: boolean;
  retryMessage: string | null;
  workerId: number;
  skillId: number;
  onUpdate: () => void;
  onError: (title: string, message: string) => void;
};

const QuizAssessmentSection = ({
  skillRequestId,
  assessment,
  assessmentPassed,
  canRetry,
  retryMessage,
  workerId,
  skillId,
  onUpdate,
  onError,
}: Props) => {
  const [processing, setProcessing] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState<{
    open: boolean;
    action: 'pass' | 'fail' | 'admin_pass';
  }>({ open: false, action: 'pass' });

  const handleToggleAssessment = async (passed: boolean) => {
    setProcessing(true);
    setShowConfirmDialog({ open: false, action: 'pass' });

    try {
      const response = await axiosRubyClient.patch(
        `/api/admin/skill_requests/${skillRequestId}/assessment`,
        { passed },
      );

      if (response.data.success) {
        onUpdate();
      } else {
        onError('Error', `Failed to ${passed ? 'pass' : 'fail'} assessment`);
      }
    } catch (error) {
      console.error('Error updating assessment:', error);
      onError('Error', `Failed to ${passed ? 'pass' : 'fail'} assessment`);
    } finally {
      setProcessing(false);
    }
  };

  const handleAdminPass = async () => {
    setProcessing(true);
    setShowConfirmDialog({ open: false, action: 'admin_pass' });

    try {
      const response = await axiosRubyClient.post(
        `/api/admin/workers/${workerId}/skills/${skillId}/admin_pass`,
      );

      if (response.data.success) {
        onUpdate();
      } else {
        onError('Error', 'Failed to mark worker as passed for skill');
      }
    } catch (error) {
      console.error('Error with admin pass:', error);
      onError('Error', 'Failed to mark worker as passed for skill');
    } finally {
      setProcessing(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const getStatusConfig = () => {
    if (!assessment) {
      return {
        bgColor: '#F3F4F6',
        borderColor: '#E5E7EB',
        icon: Quiz,
        iconColor: '#6B7280',
      };
    }
    if (assessmentPassed) {
      return {
        bgColor: '#ECFDF5',
        borderColor: '#A7F3D0',
        icon: CheckCircle,
        iconColor: '#10B981',
      };
    }
    return {
      bgColor: '#FEF2F2',
      borderColor: '#FECACA',
      icon: Cancel,
      iconColor: '#EF4444',
    };
  };

  const statusConfig = getStatusConfig();
  const StatusIcon = statusConfig.icon;

  return (
    <>
      <Card>
        <Card.Section>
          {/* Header */}
          <Stack
            style={{
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '16px',
            }}
          >
            <Stack gap={8} style={{ alignItems: 'center' }}>
              <Quiz style={{ color: '#6366F1', fontSize: '20px' }} />
              <Body weight="semibold">Multiple Choice Quiz</Body>
            </Stack>
            {/* Temporarily commented out Admin Pass
            {!assessmentPassed && (
              <Button
                variant="outlined"
                size="small"
                startIcon={
                  processing ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    <AdminPanelSettings />
                  )
                }
                onClick={() =>
                  setShowConfirmDialog({ open: true, action: 'admin_pass' })
                }
                disabled={processing}
              >
                Admin Pass
              </Button>
            )}
            */}
          </Stack>

          {assessment ? (
            /* Assessment Taken */
            <div
              style={{
                backgroundColor: statusConfig.bgColor,
                border: `1px solid ${statusConfig.borderColor}`,
                borderRadius: '12px',
                padding: '16px',
              }}
            >
              {/* Status Header */}
              <Stack
                style={{
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '16px',
                }}
              >
                <Stack gap={8} style={{ alignItems: 'center' }}>
                  <StatusIcon
                    style={{ color: statusConfig.iconColor, fontSize: '24px' }}
                  />
                  <Body weight="semibold">
                    {assessmentPassed ? 'Quiz Passed' : 'Quiz Failed'}
                  </Body>
                </Stack>
                <Stack gap={8}>
                  <Badge
                    label={assessmentPassed ? 'Passed' : 'Failed'}
                    status={assessmentPassed ? 'theme' : 'danger'}
                  />
                  {!assessmentPassed && (
                    <Badge
                      label={canRetry ? 'Can Retry' : 'Retry Blocked'}
                      status={canRetry ? 'warning' : 'neutral'}
                    />
                  )}
                </Stack>
              </Stack>

              {/* Score Display */}
              {assessment.score !== null && (
                <div
                  style={{
                    backgroundColor: '#fff',
                    borderRadius: '8px',
                    padding: '16px',
                    marginBottom: '16px',
                    textAlign: 'center',
                  }}
                >
                  <Small color="lighter" style={{ marginBottom: '4px' }}>
                    Score
                  </Small>
                  <div
                    style={{
                      fontSize: '32px',
                      fontWeight: 'bold',
                      color: assessmentPassed ? '#10B981' : '#EF4444',
                    }}
                  >
                    {assessment.score.percentage}%
                  </div>
                </div>
              )}

              {/* Metadata */}
              <Stack vertical gap={8} style={{ marginBottom: '16px' }}>
                {assessment.passed_at && (
                  <Stack style={{ justifyContent: 'space-between' }}>
                    <Small color="lighter">Completed</Small>
                    <Small>{formatDate(assessment.passed_at)}</Small>
                  </Stack>
                )}
                {!assessmentPassed && retryMessage && (
                  <Stack style={{ justifyContent: 'space-between' }}>
                    <Small color="lighter">Retry Status</Small>
                    <Small>{retryMessage}</Small>
                  </Stack>
                )}
              </Stack>

              {/* Action Buttons */}
              <Stack gap={12}>
                {!assessmentPassed && (
                  <Button
                    variant="contained"
                    color="success"
                    size="small"
                    startIcon={
                      processing ? (
                        <CircularProgress size={16} color="inherit" />
                      ) : (
                        <CheckCircle />
                      )
                    }
                    onClick={() =>
                      setShowConfirmDialog({ open: true, action: 'pass' })
                    }
                    disabled={processing}
                    style={{ flex: 1 }}
                  >
                    Override: Mark as Passed
                  </Button>
                )}
                {/* Temporarily commented out Override: Mark as Failed
                {assessmentPassed && (
                  <Button
                    variant="outlined"
                    color="error"
                    size="small"
                    startIcon={
                      processing ? (
                        <CircularProgress size={16} color="inherit" />
                      ) : (
                        <Cancel />
                      )
                    }
                    onClick={() =>
                      setShowConfirmDialog({ open: true, action: 'fail' })
                    }
                    disabled={processing}
                    style={{ flex: 1 }}
                  >
                    Override: Mark as Failed
                  </Button>
                )}
                */}
              </Stack>
            </div>
          ) : (
            /* No Assessment Taken */
            <div
              style={{
                backgroundColor: '#F9FAFB',
                border: '1px solid #E5E7EB',
                borderRadius: '12px',
                padding: '24px',
                textAlign: 'center',
              }}
            >
              <Quiz
                style={{
                  color: '#9CA3AF',
                  fontSize: '48px',
                  marginBottom: '12px',
                }}
              />
              <Body
                weight="semibold"
                style={{ marginBottom: '8px', color: '#6B7280' }}
              >
                No Quiz Taken Yet
              </Body>
              <Small color="lighter">
                The worker has not completed the multiple choice quiz for this
                skill.
              </Small>
            </div>
          )}
        </Card.Section>
      </Card>

      {/* Confirmation Dialog */}
      <Dialog
        open={showConfirmDialog.open}
        onClose={() =>
          setShowConfirmDialog({ ...showConfirmDialog, open: false })
        }
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {showConfirmDialog.action === 'pass'
            ? 'Mark Quiz as Passed'
            : showConfirmDialog.action === 'fail'
              ? 'Mark Quiz as Failed'
              : 'Admin Pass'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {showConfirmDialog.action === 'pass'
              ? 'Are you sure you want to override the quiz result and mark it as passed? This will allow the worker to proceed with their skill application.'
              : showConfirmDialog.action === 'fail'
                ? 'Are you sure you want to override the quiz result and mark it as failed? The worker may need to retake the quiz.'
                : 'Are you sure you want to skip the quiz requirement for this worker? They will be marked as passed without completing the quiz.'}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() =>
              setShowConfirmDialog({ ...showConfirmDialog, open: false })
            }
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              if (showConfirmDialog.action === 'admin_pass') {
                handleAdminPass();
              } else {
                handleToggleAssessment(showConfirmDialog.action === 'pass');
              }
            }}
            variant="contained"
            color={showConfirmDialog.action === 'fail' ? 'error' : 'primary'}
            disabled={processing}
          >
            {processing
              ? 'Processing...'
              : showConfirmDialog.action === 'pass'
                ? 'Mark as Passed'
                : showConfirmDialog.action === 'fail'
                  ? 'Mark as Failed'
                  : 'Admin Pass'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default QuizAssessmentSection;
